import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  tasks: [],
  activeTaskId: null,
  projectStatus: 'sales-phase',
  hasTeamAssigned: false,
  hasTaskTeamDefined: false,
  status: 'idle',
  error: null,
  isLoading: false
};

const projectTasksSlice = createSlice({
  name: 'projectTasks',
  initialState,
  reducers: {
    setTasks: (state, action) => {
      const { tasks, activeTaskId, projectStatus, hasTeamAssigned } = action.payload;
      state.tasks = tasks;
      state.activeTaskId = activeTaskId;
      state.projectStatus = projectStatus;
      state.hasTeamAssigned = hasTeamAssigned;
      state.status = 'succeeded';
      state.error = null;
      state.isLoading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.status = 'failed';
      state.isLoading = false;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
      state.status = action.payload ? 'loading' : state.status;
    },
    updateTask: (state, action) => {
      const { taskId, updates } = action.payload;
      const taskIndex = state.tasks.findIndex(t => t.taskId === taskId);
      if (taskIndex !== -1) {
        const existingDescriptions = state.tasks[taskIndex].descriptions || [];
        
        let newUpdates = { ...updates };
        
        // Handle description updates
        if (updates.description) {
          newUpdates.descriptions = [
            { 
              text: updates.description, 
              timestamp: new Date().toISOString(),
              username: updates.username
            },
            ...existingDescriptions
          ];
          delete newUpdates.description;
        }

        // Handle file uploads if present
        if (updates.files?.length > 0) {
          newUpdates.files = [
            ...(state.tasks[taskIndex].files || []),
            ...updates.files
          ];
        }

        state.tasks[taskIndex] = {
          ...state.tasks[taskIndex],
          ...newUpdates,
          updatedAt: new Date().toISOString()
        };
      }
    },
    // updateSubtask: (state, action) => {
    //   const { taskId, subtaskId, updates } = action.payload;
    //   const taskIndex = state.tasks.findIndex(t => t.taskId === taskId);
      
    //   if (taskIndex !== -1) {
    //     const subtaskIndex = state.tasks[taskIndex].subtasks.findIndex(
    //       s => s.id === subtaskId
    //     );
        
    //     if (subtaskIndex !== -1) {
    //       const existingSubtask = state.tasks[taskIndex].subtasks[subtaskIndex];
    //       const existingDescriptions = existingSubtask.descriptions || [];
          
    //       let newUpdates = { ...updates };
          
    //       // Handle description updates
    //       if (updates.description) {
    //         newUpdates.descriptions = [
    //           { 
    //             text: updates.description, 
    //             timestamp: new Date().toISOString(),
    //             username: updates.username
    //           },
    //           ...existingDescriptions
    //         ];
    //         delete newUpdates.description;
    //       }

    //       // Handle file uploads if present
    //       if (updates.file) {
    //         newUpdates.files = [
    //           ...(existingSubtask.files || []),
    //           {
    //             name: updates.file.name,
    //             url: updates.fileUrl, // Assuming fileUrl is provided in updates
    //             uploadedAt: new Date().toISOString(),
    //             folder: updates.folder || 'General'
    //           }
    //         ];
    //         delete newUpdates.file;
    //         delete newUpdates.fileUrl;
    //       }

    //       state.tasks[taskIndex].subtasks[subtaskIndex] = {
    //         ...existingSubtask,
    //         ...newUpdates,
    //         updatedAt: new Date().toISOString()
    //       };
          
    //       state.tasks[taskIndex].updatedAt = new Date().toISOString();
    //     }
    //   }
    // }
    updateSubtask: (state, action) => {
      const { taskId, subtaskId, updates } = action.payload;
      const taskIndex = state.tasks.findIndex(t => t.taskId === taskId);
      
      if (taskIndex !== -1) {
        const subtaskIndex = state.tasks[taskIndex].subtasks.findIndex(
          s => s.id === subtaskId
        );
        
        if (subtaskIndex !== -1) {
          const existingSubtask = state.tasks[taskIndex].subtasks[subtaskIndex];
          const existingDescriptions = existingSubtask.descriptions || [];
          
          let newUpdates = { ...updates };
          
          // Handle description updates
          if (updates.description) {
            newUpdates.descriptions = [
              { 
                text: updates.description, 
                timestamp: new Date().toISOString(),
                username: updates.username
              },
              ...existingDescriptions
            ];
            delete newUpdates.description;
          }
    
          // Handle file uploads if present
          if (updates.files?.length > 0) {
            newUpdates.files = [
              ...(existingSubtask.files || []),
              ...updates.files.map(file => ({
                name: file.name,
                url: file.url,
                path: file.path,
                uploadedAt: file.uploadedAt,
                folder: updates.folder || 'General',
                size: file.size,
                type: file.type
              }))
            ];
          }
    
          // Update the subtask
          state.tasks[taskIndex].subtasks[subtaskIndex] = {
            ...existingSubtask,
            ...newUpdates,
            updatedAt: new Date().toISOString()
          };
          
          // Update task timestamp
          state.tasks[taskIndex].updatedAt = new Date().toISOString();
        }
      }
    }
    ,
    setProjectStatus: (state, action) => {
      state.projectStatus = action.payload;
    },
    setTeamAssigned: (state, action) => {
      state.hasTeamAssigned = action.payload;
    },
    setTeamTaskAssigned: (state, action) => {
      state.hasTaskTeamDefined = action.payload;
    },
    startTask: (state, action) => {
      const { taskId, timestamp } = action.payload;
      const taskIndex = state.tasks.findIndex(t => t.taskId === taskId);
      if (taskIndex !== -1) {
        state.tasks[taskIndex].start = timestamp;
      }
    },
    reset: () => initialState
  }
});

export const {
  setTasks,
  setError,
  setLoading,
  updateTask,
  updateSubtask,
  setProjectStatus,
  setTeamAssigned,
  setTeamTaskAssigned,
  startTask,
  reset
} = projectTasksSlice.actions;

// Selectors
export const selectTasks = (state) => state.projectTasks.tasks;
export const selectActiveTaskId = (state) => state.projectTasks.activeTaskId;
export const selectProjectStatus = (state) => state.projectTasks.projectStatus;
export const selectHasTeamAssigned = (state) => state.projectTasks.hasTeamAssigned;
export const selectHasTeamTaskAssigned = (state) => state.projectTasks.hasTaskTeamDefined;
export const selectTasksStatus = (state) => state.projectTasks.status;
export const selectTasksError = (state) => state.projectTasks.error;
export const selectIsLoading = (state) => state.projectTasks.isLoading;

export default projectTasksSlice.reducer;